import React, { useState, useRef, useCallback, useEffect } from 'react';
import * as cam from '@mediapipe/camera_utils';
import { ImageSegmenter, FaceLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';

// Segmentation categories
const SEGMENT_CATEGORIES = {
  BACKGROUND: 0,
  HAIR: 1,
  BODY_SKIN: 2,
  FACE_SKIN: 3,
  CLOTHES: 4,
  ACCESSORIES: 5
};

const STEPS = [
  { view: 'Front', instruction: 'Look Straight', refImage: '/front/assets/Images/hair_scan_front.png' },
  { view: 'Hairline', instruction: 'Look Straight & Pull Hair Back', refImage: '/front/assets/Images/hair_scan_hairline.png' },
  { view: 'Right', instruction: 'Turn Left', refImage: '/front/assets/Images/hair_scan_right.png' },
  { view: 'Left', instruction: 'Turn Right', refImage: '/front/assets/Images/hair_scan_left.png' },
  { view: 'Top', instruction: 'Show Top View', refImage: '/front/assets/Images/hair_scan_top.png' }
];

// Adaptive settings based on device
const getDeviceConfig = () => {
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  return {
    cameraWidth: isMobile ? 640 : 1280,
    cameraHeight: isMobile ? 480 : 720,
    cameraFPS: isMobile ? 30 : 60,
    landmarkInterval: isMobile ? 2 : 4,    // Snappier: Mobile 15fps, Desktop 15fps
    analysisInterval: isMobile ? 15 : 30,  // ~2 analyses/sec
    uiUpdateInterval: isMobile ? 400 : 200,
  };
};

const DEVICE_CONFIG = getDeviceConfig();

// Isolated component to prevent AI-related hitches from affecting logic-light animations
const GalleryItem = React.memo(({ idx, step, collectedImage, isCurrent, stepIndex }) => {
  const isCaptured = idx < collectedImage.length;
  
  return (
    <div className={`gallery-item ${isCaptured ? 'captured' : ''} ${isCurrent ? 'current' : ''}`}>
      <div className="gallery-image-wrapper">
        {isCurrent && (
          <svg className="snake-border-svg" viewBox="0 0 100 100">
            <rect x="2" y="2" width="96" height="96" rx="10" pathLength="100" />
          </svg>
        )}
        {collectedImage[idx] ? (
          <img src={collectedImage[idx]} alt={step.view} />
        ) : (
          <img 
            src={step.refImage} 
            alt={step.view} 
            className={`ref-image ${!isCurrent ? 'is-blurred' : ''}`} 
          />
        )}
      </div>
      <span className="gallery-label">{step.view}</span>
    </div>
  );
});

const ScalpScanner = ({ onComplete, onClose }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [status, setStatus] = useState('Initializing');
  const [serverGuidance, setServerGuidance] = useState('');
  const [metrics, setMetrics] = useState({ pitch: 0, yaw: 0, view: '-', foreheadRatio: 0 });
  const [collectedImages, setCollectedImages] = useState([]);
  const [isAligned, setIsAligned] = useState(false);
  const [stabilityDisplay, setStabilityDisplay] = useState(0);
  const [hairDetection, setHairDetection] = useState({ isSkinVisible: false, hairPercent: 0 });
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isFlashing, setIsFlashing] = useState(false);
  const [showTick, setShowTick] = useState(false);

  const canvasRef = useRef(null);
  const webcamRef = useRef(null);
  const stableCount = useRef(0);
  const smoothedPose = useRef({ pitch: 0, yaw: 0 });
  const stepIndexRef = useRef(0);
  const isTransitioningRef = useRef(false);
  const collectedImagesRef = useRef([]);
  const frameCountRef = useRef(0);
  const lastHairAnalysisRef = useRef({ isSkinVisible: false, hairPercent: 100 });
  const isProcessingRef = useRef(false);
  const lastUIUpdateRef = useRef(0);
  const lastMetricsUpdateRef = useRef(0);
  const currentStatusRef = useRef('Initializing');
  const currentAlignedRef = useRef(false);
  const cameraRef = useRef(null);
  const imageSegmenterRef = useRef(null);
  const analysisPromisesRef = useRef([]);
  
  const EMA_ALPHA = 0.25;
  const completedRef = useRef(false);

  // Optimized Status Updater
  const updateStatus = useCallback((newStatus) => {
    if (currentStatusRef.current !== newStatus) {
        currentStatusRef.current = newStatus;
        setStatus(newStatus);
    }
  }, []);
  
  // High-Fidelity Timing Refs
  const lastProcessedTime = useRef(performance.now());
  const cumulativeWarmupTime = useRef(0); // Total milliseconds spent aligned
  const stabilityStartTime = useRef(null); // When the current continuous alignment started
  const isWarmedUp = useRef(false);

  useEffect(() => {
    completedRef.current = completed;
  }, [completed]);

  useEffect(() => {
    stepIndexRef.current = stepIndex;
  }, [stepIndex]);

  const captureScreenshot = useCallback(() => {
    const video = webcamRef.current;
    if (!video || video.readyState < 2) return null;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.95);
  }, []);

  const calculatePose = useCallback((landmarks, analysisResult) => {
    const now = performance.now();
    const deltaTime = now - lastProcessedTime.current;
    lastProcessedTime.current = now;

    const nose = landmarks[1];
    const leftEye = landmarks[33];
    const rightEye = landmarks[263];
    const chin = landmarks[152];
    const foreheadTop = landmarks[10];
    const leftBrow = landmarks[70];
    const rightBrow = landmarks[300];
    
    const faceHeight = chin.y - foreheadTop.y;
    const eyeWidth = Math.max(Math.abs(rightEye.x - leftEye.x), 0.01);
    const eyeMidX = (leftEye.x + rightEye.x) / 2;
    const yaw = ((nose.x - eyeMidX) / eyeWidth) * 100;

    const eyeMidY = (leftEye.y + rightEye.y) / 2;
    const noseToEyeY = nose.y - eyeMidY;
    const noseToChinY = Math.max(chin.y - nose.y, 0.01);
    const rawPitch = ((noseToEyeY / noseToChinY) - 0.4) * 150;

    smoothedPose.current.pitch = (rawPitch * EMA_ALPHA) + (smoothedPose.current.pitch * (1 - EMA_ALPHA));
    smoothedPose.current.yaw = (yaw * EMA_ALPHA) + (smoothedPose.current.yaw * (1 - EMA_ALPHA));

    const finalPitch = Math.round(smoothedPose.current.pitch);
    const finalYaw = Math.round(smoothedPose.current.yaw);
    
    const browMidY = (leftBrow.y + rightBrow.y) / 2;
    const foreheadHeight = browMidY - foreheadTop.y;
    const foreheadRatio = foreheadHeight / Math.max(faceHeight, 0.01);
    
    if (analysisResult) {
      if (now - lastUIUpdateRef.current > DEVICE_CONFIG.uiUpdateInterval) {
        setHairDetection({ 
          isSkinVisible: analysisResult.isSkinVisible, 
          hairPercent: analysisResult.hairPercent 
        });
        lastUIUpdateRef.current = now;
      }
      lastHairAnalysisRef.current = analysisResult;
    }

    const { isSkinVisible } = lastHairAnalysisRef.current;
    const targetView = STEPS[stepIndexRef.current]?.view;
    let detectedView = "None";

    const isFrontOrientation = Math.abs(finalPitch) < 25 && Math.abs(finalYaw) < 35;
    const isTopOrientation = finalPitch > 80; 
    const isLeftOrientation = finalYaw < -24;
    const isRightOrientation = finalYaw > 24;
    const isHairlineOrientation = Math.abs(finalYaw) < 20 && finalPitch >= 10 && finalPitch <= 75; // More flexible range


    if (targetView === "Front" && isFrontOrientation) {
      detectedView = "Front";
    } else if (targetView === "Hairline" && isHairlineOrientation && isSkinVisible) {
      detectedView = "Hairline";
    } else if (targetView === "Top" && isTopOrientation) {
      detectedView = "Top";
    } else if (targetView === "Left" && isLeftOrientation) {
      detectedView = "Left";
    } else if (targetView === "Right" && isRightOrientation) {
      detectedView = "Right";
    } else {
      if (isHairlineOrientation && isSkinVisible) detectedView = "Hairline";
      else if (isTopOrientation) detectedView = "Top";
      else if (isLeftOrientation) detectedView = "Left";
      else if (isRightOrientation) detectedView = "Right";
      else if (isFrontOrientation) detectedView = "Front";
    }

    const metricsTimestamp = performance.now();
    if (metricsTimestamp - lastMetricsUpdateRef.current > 100) { // Throttle metrics state updates to 10fps
        setMetrics({ pitch: finalPitch, yaw: finalYaw, view: detectedView, foreheadRatio: Math.round(foreheadRatio * 100) });
        lastMetricsUpdateRef.current = metricsTimestamp;
    }
    validatePose(detectedView, deltaTime);
  }, [validatePose]);

  const validatePose = useCallback((detectedView, deltaTime) => {
    if (completedRef.current || isTransitioningRef.current) return;
    const targetView = STEPS[stepIndexRef.current]?.view;
    if (!targetView) return;

    const aligned = detectedView === targetView;
    if (currentAlignedRef.current !== aligned) {
        currentAlignedRef.current = aligned;
        setIsAligned(aligned);
    }

    const WARMUP_THRESHOLD = 3000; // 3 seconds
    const STABILITY_THRESHOLD = 1000; // 1 second

    // 1. CUMULATIVE WARMUP PHASE (One-time delay for the very first capture)
    if (stepIndexRef.current === 0 && !isWarmedUp.current) {
        if (aligned) {
            cumulativeWarmupTime.current += deltaTime;
            if (cumulativeWarmupTime.current >= WARMUP_THRESHOLD) {
                isWarmedUp.current = true;
                // Warmup over. Reset stability to require a fresh, crisp 0.4s alignment 
                // before we actually take the first photo.
                stabilityStartTime.current = null;
            }
        }
        
        // If still in warmup, show progress but prioritize alignment feedback
        if (!isWarmedUp.current) {
            const progress = Math.min(cumulativeWarmupTime.current / WARMUP_THRESHOLD, 1);
            setStabilityDisplay(progress);
            
            if (aligned) {
                updateStatus("ADJUSTING...");
                setServerGuidance("Readying scanner...");
            } else {
                if (detectedView !== "None") {
                updateStatus("Wrong Angle");
                setServerGuidance(`Showing ${detectedView}. Need ${targetView}.`);
            } else {
                updateStatus("Adjust Head");
                    setServerGuidance(STEPS[stepIndexRef.current]?.instruction || "");
                }
            }
            return; 
        }
    }

    // 2. STABILITY DETECTION (For all snaps)
    if (aligned) {
      if (!stabilityStartTime.current) {
          stabilityStartTime.current = performance.now();
      }
      
      const elapsed = performance.now() - stabilityStartTime.current;
      const progress = Math.min(elapsed / STABILITY_THRESHOLD, 1);
      setStabilityDisplay(progress);

      if (elapsed >= STABILITY_THRESHOLD) { 
        isTransitioningRef.current = true;
        updateStatus("CAPTURING...");
        const imageSrc = captureScreenshot();
        if (imageSrc) {
          handleStepComplete(imageSrc);
        } else {
          isTransitioningRef.current = false;
          stabilityStartTime.current = null;
        }
      } else {
        updateStatus("STAY STILL...");
        setServerGuidance("Capturing soon...");
      }
    } else {
      stabilityStartTime.current = null; // Continuous alignment broken
      setStabilityDisplay(0);

      if (detectedView !== "None") {
        updateStatus("Wrong Angle");
        setServerGuidance(`Showing ${detectedView}. Need ${targetView}.`);
      } else {
        updateStatus("Adjust Head");
        setServerGuidance(STEPS[stepIndexRef.current]?.instruction || "");
      }
    }
  }, [captureScreenshot]);

  const handleStepComplete = useCallback((image) => {
    setIsFlashing(true);
    setShowTick(true);
    setTimeout(() => setIsFlashing(false), 250);
    setTimeout(() => setShowTick(false), 2400);

    collectedImagesRef.current = [...collectedImagesRef.current, image];
    setCollectedImages([...collectedImagesRef.current]);

    if (stepIndexRef.current < STEPS.length - 1) {
      stableCount.current = 0;
      setStabilityDisplay(0);
      setStepIndex(prev => prev + 1);
      updateStatus("NEXT STEP");
      setTimeout(() => { isTransitioningRef.current = false; }, 1500);
    } else {
      setCompleted(true);
      updateStatus("DONE");
      
      // Pass raw data for Blade to process
      window.scalpAnalysisData = collectedImagesRef.current.map((img, i) => ({
        view: STEPS[i]?.view,
        original: img,
        // No mesh data yet - Blade will generate it
        maskData: null,
        width: 0, // Will be set in Blade
        height: 0
      }));

      setTimeout(() => { 
        onComplete(collectedImagesRef.current);
      }, 3000);
    }
  }, [onComplete]);

  const analyzeForehead = useCallback((segmenter, video, landmarks, timestamp) => {
    if (!segmenter || !video || !landmarks) return null;
    try {
      let result;
      // MediaPipe segmenter methods exist in both modes but throw if used incorrectly
      const isGlobal = (segmenter === window.backgroundSegmenter);
      
      if (!isGlobal) {
          try {
            result = segmenter.segmentForVideo(video, timestamp);
          } catch (e) {
            // Fallback to image mode
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            canvas.getContext('2d').drawImage(video, 0, 0);
            result = segmenter.segment(canvas);
          }
      } else {
          // Global is always IMAGE mode
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          canvas.getContext('2d').drawImage(video, 0, 0);
          result = segmenter.segment(canvas);
      }
      
      if (!result || !result.categoryMask) return null;
      const mask = result.categoryMask;
      const maskData = mask.getAsUint8Array();
      const maskWidth = mask.width, maskHeight = mask.height;
      let hairCount = 0, totalPixels = 0, skinCount = 0;
      const step = 4;

      const foreheadTop = landmarks[10], foreheadLeft = landmarks[67], foreheadRight = landmarks[297];
      const leftBrow = landmarks[70], rightBrow = landmarks[300];
      const minX = Math.floor(Math.max(0, (foreheadLeft.x - 0.1)) * maskWidth);
      const maxX = Math.ceil(Math.min(1, (foreheadRight.x + 0.1)) * maskWidth);
      const minY = Math.floor(Math.max(0, (foreheadTop.y - 0.05)) * maskHeight);
      const browMidY = (leftBrow.y + rightBrow.y) / 2;
      const maxY = Math.ceil((browMidY - 0.02) * maskHeight);

      for (let y = Math.max(0, minY); y < Math.min(maskHeight, maxY); y += step) {
        for (let x = Math.max(0, minX); x < Math.min(maskWidth, maxX); x += step) {
          const idx = y * maskWidth + x;
          const cat = maskData[idx];
          if (cat === 1) hairCount++;
          else if (cat === 3 || cat === 2) skinCount++;
          totalPixels++;
        }
      }

      let res = null;
      if (totalPixels > 0) {
        const hairPercent = Math.round((hairCount / totalPixels) * 100);
        const skinPercent = Math.round((skinCount / totalPixels) * 100);
        res = { isSkinVisible: hairPercent < 45 && skinPercent > 40, hairPercent };
      }
      mask.close();
      return res;
    } catch (e) { 
      console.error("Analysis Error:", e);
      return null; 
    }
  }, []);


  useEffect(() => {
    let isMounted = true;
    let cameraInstance = null, faceLandmarker = null, imageSegmenter = null;
    
    const initVision = async () => {
      try {
        if (window.globalFaceLandmarker && window.backgroundSegmenter) {
             console.log("Using Global Preloaded AI Models");
             faceLandmarker = window.globalFaceLandmarker;
             imageSegmenter = window.backgroundSegmenter;
        } else {
             console.warn("Global models not ready, loading locally...");
             const vision = await FilesetResolver.forVisionTasks("https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm");
             const [landmarker, segmenter] = await Promise.all([
               FaceLandmarker.createFromOptions(vision, { 
                 baseOptions: { 
                   modelAssetPath: `https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task`, 
                   delegate: "GPU" 
                 }, 
                 runningMode: "VIDEO", 
                 numFaces: 1 
               }),
               ImageSegmenter.createFromOptions(vision, { 
                 baseOptions: { 
                   modelAssetPath: "https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_multiclass_256x256/float32/latest/selfie_multiclass_256x256.tflite", 
                   delegate: "CPU" 
                 }, 
                 runningMode: window.backgroundSegmenter ? "IMAGE" : "VIDEO", // If locally loading, use VIDEO, global is IMAGE
                 outputCategoryMask: true 
               })
             ]);
             imageSegmenter = segmenter;
        }

        imageSegmenterRef.current = imageSegmenter;

        if (webcamRef.current) {
          cameraInstance = new cam.Camera(webcamRef.current, {
            onFrame: async () => {
              if (completedRef.current || !webcamRef.current || !faceLandmarker || isProcessingRef.current) return;
              frameCountRef.current++;
              if (frameCountRef.current % DEVICE_CONFIG.landmarkInterval !== 0) return;
              isProcessingRef.current = true;
              try {
                const ts = performance.now();
                const deltaTime = ts - lastProcessedTime.current;
                lastProcessedTime.current = ts;

                const landmarkResult = faceLandmarker.detectForVideo(webcamRef.current, ts);
                if (isMounted && landmarkResult.faceLandmarks?.length > 0) {
                  let analysisResult = (frameCountRef.current % DEVICE_CONFIG.analysisInterval === 0 && imageSegmenter) ? analyzeForehead(imageSegmenter, webcamRef.current, landmarkResult.faceLandmarks[0], ts) : null;
                  calculatePose(landmarkResult.faceLandmarks[0], analysisResult);
                } else if (isMounted) { 
                  // If face is lost, treat as misalignment to reset stability timer
                  validatePose("None", deltaTime);
                  updateStatus("Align face"); 
                }
              } finally { isProcessingRef.current = false; }
            },
            width: DEVICE_CONFIG.cameraWidth, height: DEVICE_CONFIG.cameraHeight
          });
          cameraInstance.start(); cameraRef.current = cameraInstance;
        }
      } catch (error) { console.error("Init Vision Error:", error); }
    };
    initVision();
    return () => { 
      isMounted = false; 
      if (cameraInstance) cameraInstance.stop(); 
      // Only close models if they are NOT the global preloaded ones
      if (faceLandmarker && faceLandmarker !== window.globalFaceLandmarker) faceLandmarker.close(); 
      if (imageSegmenter && imageSegmenter !== window.backgroundSegmenter) imageSegmenter.close(); 
    };
  }, [calculatePose, validatePose, analyzeForehead]);

  return (
    <div className="scalp-scanner-overlay">
      <div className="scanner-container">
        <button className="btn-close-scanner" onClick={onClose}>&times;</button>
        <div className="scanner-header">
           <h2>AI Scalp Scan</h2>
           <div className="step-badge">Step {stepIndex + 1}/{STEPS.length}</div>
        </div>

        <div className={`scanner-viewport ${isFlashing ? 'shutter' : ''}`}>
          <div className={`status-pill ${status === 'NEXT STEP' ? 'success-subtle' : isAligned ? 'aligned' : status === 'Wrong Angle' || status === 'Adjust Head' ? 'warning' : status === 'Align face' ? 'error' : ''}`}>{status}</div>
          <div className="scanner-feed-container">
            <video ref={webcamRef} autoPlay playsInline muted className="scanner-webcam" />
            <canvas ref={canvasRef} className="scanner-canvas" width="640" height="480" />
            <div className="scan-line"></div>
          </div>
          
          <div className="viewport-corners"><span></span></div>
          
          <div className="instruction-gap">
            {STEPS[stepIndex]?.instruction}
          </div>

          {showTick && (
            <div className="success-tick">
              <svg viewBox="0 0 52 52">
                <circle cx="26" cy="26" r="25" fill="none" />
                <path fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
              </svg>
            </div>
          )}
        </div>

        <div className="scanner-footer">
          <div className="captured-gallery">
            <h4 className="gallery-title">Captured Images:</h4>
            <div className="gallery-row">
              {STEPS.map((step, idx) => (
                <GalleryItem 
                  key={step.view}
                  idx={idx}
                  step={step}
                  collectedImage={collectedImages}
                  isCurrent={idx === stepIndex}
                  stepIndex={stepIndex}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScalpScanner;
